import React, { useEffect, useRef, useState } from 'react';
import { FileText, Loader2, AlertCircle, ClipboardList } from 'lucide-react';
import {
  TabContentWrapper,
  ExtractionHeader,
  ExtractionTitle,
  ExtractedBadge,
  ExtractionList,
  ExtractionItem,
  ItemSource,
  ItemSourceDoc,
  ItemSourcePage,
  ItemSourceDot,
  ItemTitle,
  ExtractionFieldGrid,
  ExtractionField,
  FieldLabel,
  FieldValue,
  StatusMessage,
  ExtractButton,
  LoadingState,
  EmptyExtractionState,
  ExtractionTabsWrapper,
  ExtractionTabItem,
} from './processing.styles';

import { useGetStatus, useExtractions } from './hook/Useprocessing';

const IN_PROGRESS_MSGS = ['Extraction in Progress!', 'Data Extraction in progress!'];

const isInProgressMsg = (msg = '') =>
  IN_PROGRESS_MSGS.some((m) => msg.toLowerCase().includes(m.toLowerCase()));

const Extraction = ({ listId, initialExtractionStatus, onRefetchList }) => {
  const skipStatusFetch = !!initialExtractionStatus;

  const { data: statusData, isLoading: isLoadingStatus } = useGetStatus(listId, {
    enabled: !skipStatusFetch,
  });

  const extractionStatus = initialExtractionStatus ?? statusData?.extractionStatus;

  const { extractMutation, extractionsQuery } = useExtractions(listId, extractionStatus);

  const [activeTabId, setActiveTabId] = useState(null);
  const observerRef = useRef(null);

  // Auto-trigger extractData once status is COMPLETE and we have no cached data yet
  useEffect(() => {
    if (
      extractionStatus === 'COMPLETE' &&
      !extractionsQuery.data &&
      !extractionsQuery.isFetching &&
      !extractMutation.isPending &&
      !extractMutation.isSuccess &&
      !extractMutation.isError
    ) {
      extractMutation.mutate();
    }
  }, [extractionStatus, extractionsQuery.data, extractionsQuery.isFetching]); // eslint-disable-line

  // ── Detect "in progress" from either the mutation result or query error ───
  const mutationInProgress =
    extractMutation.isSuccess &&
    isInProgressMsg(extractMutation.data?.message ?? extractMutation.data?.detail ?? '');

  const queryInProgress =
    extractionsQuery.isError &&
    isInProgressMsg(extractionsQuery.error?.message ?? extractionsQuery.error?.detail ?? '');

  const extractionInProgress = mutationInProgress || queryInProgress;

  const extractions = extractionsQuery.data?.extractions ?? [];
  const totalItems = extractions.reduce((acc, e) => acc + (e.fileData?.length ?? 0), 0);

  // ── Observer for sticky tab highlighting ─────────────────────────────────
  useEffect(() => {
    if (extractions.length === 0) return;

    setActiveTabId(extractions[0]._id);
    observerRef.current?.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          const id = visible[0].target.getAttribute('data-extraction-id');
          if (id) setActiveTabId(id);
        }
      },
      { rootMargin: '0px 0px -60% 0px', threshold: 0 }
    );

    extractions.forEach((e) => {
      const el = document.getElementById(`extraction-section-${e._id}`);
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [extractions]); // eslint-disable-line

  const handleTabClick = (id) => {
    setActiveTabId(id);
    const el = document.getElementById(`extraction-section-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleGenerateReport = () => {
    extractMutation.mutate(undefined, {
      onSuccess: () => {
        onRefetchList?.();
      },
      onError: () => {
        onRefetchList?.();
      },
    });
  };

  // ── Waiting for status ────────────────────────────────────────────────────
  if (!skipStatusFetch && isLoadingStatus) {
    return (
      <TabContentWrapper>
        <LoadingState>
          <Loader2 size={22} className="spin" />
          <span>Checking extraction status…</span>
        </LoadingState>
      </TabContentWrapper>
    );
  }

  // ── Fetching / mutating in progress ──────────────────────────────────────
  if (extractMutation.isPending || extractionsQuery.isLoading || extractionsQuery.isFetching) {
    return (
      <TabContentWrapper>
        <LoadingState>
          <Loader2 size={22} className="spin" />
          <span>Fetching extracted data…</span>
        </LoadingState>
      </TabContentWrapper>
    );
  }

  // ── "Extraction in Progress!" from API ────────────────────────────────────
  if (extractionInProgress) {
    return (
      <TabContentWrapper>
        <EmptyExtractionState>
          <Loader2 size={36} strokeWidth={1.2} className="spin" />
          <p style={{ margin: 0, fontWeight: 500 }}>Extraction In Progress</p>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-tertiary)' }}>
            Your documents are currently being extracted. Check back shortly or try again.
          </p>
          <ExtractButton
            type="button"
            onClick={handleGenerateReport}
            disabled={extractMutation.isPending}
          >
            {extractMutation.isPending ? (
              <>
                <Loader2 size={14} className="spin" /> Extracting…
              </>
            ) : (
              <>
                <FileText size={14} /> Check Again
              </>
            )}
          </ExtractButton>
        </EmptyExtractionState>
      </TabContentWrapper>
    );
  }

  // ── Data arrived ──────────────────────────────────────────────────────────
  if (extractions.length > 0) {
    return (
      <TabContentWrapper>
        <ExtractionHeader>
          <ExtractionTitle>Extraction Result</ExtractionTitle>
          <ExtractedBadge>
            <FileText size={14} />
            {totalItems} Items Extracted
          </ExtractedBadge>
        </ExtractionHeader>

        <ExtractionTabsWrapper>
          {extractions.map((extraction) => (
            <ExtractionTabItem
              key={extraction._id}
              $active={activeTabId === extraction._id}
              onClick={() => handleTabClick(extraction._id)}
              type="button"
              aria-label={`Scroll to ${extraction.documentName}`}
            >
              {extraction.documentName}
            </ExtractionTabItem>
          ))}
        </ExtractionTabsWrapper>

        <ExtractionList>
          {extractions.map((extraction) => (
            <ExtractionItem
              key={extraction._id}
              id={`extraction-section-${extraction._id}`}
              data-extraction-id={extraction._id}
            >
              <ItemSource>
                <ItemSourceDoc>
                  <FileText size={12} style={{ flexShrink: 0 }} />
                  {extraction.documentName}
                </ItemSourceDoc>
                <ItemSourceDot>•</ItemSourceDot>
                <ItemSourcePage>
                  {new Date(extraction.createdAt).toLocaleDateString('en-AU', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </ItemSourcePage>
              </ItemSource>

              <ItemTitle>{extraction.documentName}</ItemTitle>

              <ExtractionFieldGrid>
                {extraction.fileData?.map((field) => (
                  <ExtractionField key={field.extractionName}>
                    <FieldLabel>{field.title}</FieldLabel>
                    <FieldValue>{field.extractedValue}</FieldValue>
                  </ExtractionField>
                ))}
              </ExtractionFieldGrid>
            </ExtractionItem>
          ))}
        </ExtractionList>
      </TabContentWrapper>
    );
  }

  // ── Status not COMPLETE and no data ──────────────────────────────────────
  if (extractionStatus !== 'COMPLETE') {
    return (
      <TabContentWrapper>
        <EmptyExtractionState>
          <ClipboardList size={36} strokeWidth={1.2} />
          <p style={{ margin: 0, fontWeight: 500 }}>Extraction Pending</p>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-tertiary)' }}>
            Documents are still being processed. You can trigger extraction manually when ready.
          </p>
          <ExtractButton
            type="button"
            onClick={handleGenerateReport}
            disabled={extractMutation.isPending}
          >
            {extractMutation.isPending ? (
              <>
                <Loader2 size={14} className="spin" /> Extracting…
              </>
            ) : (
              <>
                <FileText size={14} /> Get Extraction
              </>
            )}
          </ExtractButton>
          {extractMutation.isError && !extractionsQuery.isFetching && (
            <StatusMessage $type="error">
              <AlertCircle size={14} />
              {extractMutation.error?.message || 'Extraction failed. Please try again.'}
            </StatusMessage>
          )}
        </EmptyExtractionState>
      </TabContentWrapper>
    );
  }

  // ── Query-level error (non-progress) ─────────────────────────────────────
  if (extractionsQuery.isError) {
    return (
      <TabContentWrapper>
        <StatusMessage $type="error" style={{ margin: '1.5rem 0' }}>
          <AlertCircle size={14} />
          {extractionsQuery.error?.message || 'Failed to load extraction data.'}
        </StatusMessage>
      </TabContentWrapper>
    );
  }

  // ── COMPLETE but empty ────────────────────────────────────────────────────
  return (
    <TabContentWrapper>
      <EmptyExtractionState>
        <ClipboardList size={36} strokeWidth={1.2} />
        <p style={{ margin: 0 }}>No extraction data available yet.</p>
      </EmptyExtractionState>
    </TabContentWrapper>
  );
};

export default Extraction;
